import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { FALLBACK_RATES_TO_GBP, clearPublicRatesCache, fetchPublicExchangeRates, readCachedPublicRates, } from '../utils/exchange-rates';
const ExchangeRatesContext = createContext(null);
/**
 * Single app-wide load of public FX rates (avoids duplicate fetches when multiple
 * components need conversion). Mount once near the root of the app.
 */
export function ExchangeRatesProvider({ children }) {
    const [rates, setRates] = useState(() => {
        const cached = readCachedPublicRates();
        return cached?.rates ?? { ...FALLBACK_RATES_TO_GBP };
    });
    const [status, setStatus] = useState(() => readCachedPublicRates()?.fresh ? 'succeeded' : 'loading');
    const [lastUpdated, setLastUpdated] = useState(null);
    const applyPayload = useCallback((data) => {
        setRates(data.rates);
        setLastUpdated(data.last_updated);
        setStatus('succeeded');
    }, []);
    useEffect(() => {
        let cancelled = false;
        const cached = readCachedPublicRates();
        if (cached?.fresh) {
            setRates(cached.rates);
            setStatus('succeeded');
            return;
        }
        setStatus('loading');
        fetchPublicExchangeRates()
            .then((data) => {
            if (cancelled)
                return;
            applyPayload(data);
        })
            .catch(() => {
            if (cancelled)
                return;
            setRates({ ...FALLBACK_RATES_TO_GBP });
            setStatus('failed');
        });
        return () => {
            cancelled = true;
        };
    }, [applyPayload]);
    const refetch = useCallback(async () => {
        clearPublicRatesCache();
        setStatus('loading');
        try {
            const data = await fetchPublicExchangeRates();
            applyPayload(data);
        }
        catch {
            setRates({ ...FALLBACK_RATES_TO_GBP });
            setStatus('failed');
        }
    }, [applyPayload]);
    const value = useMemo(() => ({
        rates,
        status,
        lastUpdated,
        refetch,
    }), [rates, status, lastUpdated, refetch]);
    return (_jsx(ExchangeRatesContext.Provider, { value: value, children: children }));
}
export function useExchangeRates() {
    const ctx = useContext(ExchangeRatesContext);
    if (!ctx) {
        throw new Error('useExchangeRates must be used within ExchangeRatesProvider');
    }
    return ctx;
}
